import loadjs from "loadjs";
import {appInfo, eventEmitter} from "@weapp/utils";

interface pluginType{
    name: string;//import时使用的name， 如 vue
    path: string|string[];//插件js地址， 如： /build/ecodesdk/js/vue.global.prod.js
    syncPath?: string|string[];
    winName: string;//插件加载后window对象 如：Vue
    loadStatus?: 'unload'|'pending'|'loaded'|'fail';
    instance?: any;
    version?: string;
    sync?: boolean;
    order?: number;
}

interface pluginCacheType {
    [_: string]: pluginType;
}
/**
 * 用于第三方插件管理功能，用于拦截ecode加载组件时的插件管理
 */
class PluginManager{
    //插件缓存
    _pluginCache:pluginCacheType = {};

    appName = 'weappEcodeSDK';
    eventNames = {
        /**
         * 插件加载完回调
         */
        plugin_load : 'PLUGIN_LOAD',
    };

    constructor() {
        const { publicUrl='',publicDomain='' } = appInfo('@weapp/ecodesdk');
        this.registerPlugin([
            {
                name: 'vue',
                path: `${publicDomain}${publicUrl}/build/ecodesdk/js/vue.global.prod.js`,
                winName: 'Vue',
                sync: true,
                order: -1
            },
            {
                name: 'vue-router',
                path: `${publicDomain}${publicUrl}/build/ecodesdk/js/vue-router.global.prod.js`,
                winName: 'VueRouter',
                version: '4.4.3'
            },
            {
                name: 'vuex',
                path: `${publicDomain}${publicUrl}/build/ecodesdk/js/vuex.global.prod.js`,
                winName: 'Vuex',
                version: '4.1.0'
            },
            {
                name: 'element-plus',
                path: [
                    `${publicDomain}${publicUrl}/build/ecodesdk/js/element.plus.css`,
                    `${publicDomain}${publicUrl}/build/ecodesdk/js/element.plus.min.js`
                ],
                winName: 'ElementPlus',
                version: '2.8.1'
            }
        ])
    }

    /**
     * 注册指定插件加载回调事件
     */
    registerPluginLoadEvent=(plugins:pluginType["name"][], options:{
        pluginLoad?(plugin:pluginType):void;//单个插件加载完成时回调
        allPluginLoad?(plugins:pluginType[]):void;//全部插件加载完成回调
    })=>{
        const {pluginLoad,allPluginLoad} = options;
        let loadLen = 0;
        let pluginLen = plugins.length;
        let tempPlugins:pluginType[]|undefined = [];
        let pluginLoadEvent=(newPlugin:pluginType)=>{
            pluginLoad?.(newPlugin);
            tempPlugins!.push(newPlugin);
            loadLen+=1;
            if(loadLen===pluginLen){
                allPluginLoad?.(tempPlugins!)
                tempPlugins = undefined;
            }
        }
        plugins.forEach(plugin=>{
            if(this.getPluginInstance(plugin)){
                let newPlugin = this.getPluginInfo(plugin);
                pluginLoadEvent(newPlugin);
            }else {
                if(pluginLoad||allPluginLoad) {
                    eventEmitter.once(this.appName,this.eventNames.plugin_load+"_"+plugin,pluginLoadEvent)
                }
            }

        })
    }

    /**
     * 注册插件
     */
    registerPlugin=(plugins:pluginType[], options?:{
        pluginLoad?(plugin:pluginType):void;//单个插件加载完成时回调
        allPluginLoad?(plugins:pluginType[]):void;//全部插件加载完成回调
    })=>{
        plugins.forEach(plugin=>{
            this._pluginCache[plugin.name] = plugin;
        })
        if(options) this.registerPluginLoadEvent(plugins.map(item=>item.name),options);
    }

    /**
     * 获取插件信息
     * @param name
     */
    getPluginInfo = (name: pluginType["name"])=>{
        return this._pluginCache[name];
    }

    /**
     * 获取插件实例，主要用于ecode import获取对应实例
     * @param name
     */
    getPluginInstance = (name: pluginType["name"])=>{
        return this.getPluginInfo(name)?.instance;
    }

    updatePluginCache = (name: pluginType["name"], options: Partial<pluginType> )=>{
        this._pluginCache[name] = {
            ...this._pluginCache[name],
            ...options
        }
    }

    getUnRegisterPlugin=(name: pluginType["name"]):Promise<pluginType>=>{
        return new Promise(resolve => {
            let count = 0;
            let timer = setInterval(()=>{
                let plugin = this._pluginCache[name];
                if(plugin){
                    resolve(this.getPlugin(plugin));
                }else if(count>=10){
                    clearInterval(timer);
                    console.error('this plugin "'+name+'" is not found');
                    resolve({name,winName:'',path:''})
                }else {
                    count++;
                }
            },200)
        })
    }

    /**
     * 加载同步js文件
     * @param paths
     */
    loadSyncJs=async (paths:string[])=>{
        let result = true;
        let len = paths.length;
        for (let i = 0; i < len; i++) {
            let flag:boolean = await new Promise((resolve)=>{
                loadjs(paths[i],paths[i],{
                    success:()=> {
                        resolve(true);
                    },
                    error:()=> {
                        resolve(false);
                    }
                })
            })
            if(!flag) {
                result = flag;
                break;
            }
        }
        return result;
    }

    /**
     * 加载指定插件
     * @param plugin
     */
    getPlugin=(plugin:pluginType):Promise<pluginType>=>{
        return new Promise(async (resolve)=>{
            const {path,winName, name, loadStatus, syncPath} = plugin;
            if(loadStatus==='pending'){
                eventEmitter.once(this.appName,this.eventNames.plugin_load+"_"+name,(newPlugin:pluginType)=>{
                    resolve(newPlugin);
                })
            }else {
                this.updatePluginCache(name, {loadStatus: 'pending'})
                const onLoad = (flag: boolean)=>{
                    let newPlugin: pluginType = {
                        ...plugin,
                        instance: window[winName],
                        loadStatus: flag?'loaded': 'fail'
                    };
                    this._pluginCache[name] = newPlugin;
                    eventEmitter.emit(this.appName,this.eventNames.plugin_load+'_'+name,newPlugin)
                    resolve(newPlugin)
                }
                let loadSyncPath = ()=>{
                    loadjs(path,this.appName+"_loadjs_"+name,{
                        success:()=> {
                            onLoad(true);
                        },
                        error:()=> {
                            onLoad(false);
                        }
                    })
                }
                let getArrayPath = (originPath:string|string[])=>{
                    let newSyncPath:string[] = [];
                    if(originPath){
                        if(typeof originPath==='string') {
                            newSyncPath = [originPath];
                        }else {
                            newSyncPath = originPath;
                        }
                    }
                    return newSyncPath;
                }
                let newSyncPath = getArrayPath(syncPath||'');
               if(newSyncPath?.length){
                   let flag = await this.loadSyncJs(newSyncPath);
                   if(flag){
                       let newPath = getArrayPath(path||'');
                       if(newPath.length){
                           loadSyncPath();
                       }else {
                           onLoad(true);
                       }
                   }else {
                       onLoad(false);
                   }
               }else {
                   loadSyncPath();
               }
            }
        })
    }

    /**
     * 加载同步插件
     */
    loadSyncPlugin=async (plugins: pluginType[])=>{
        let newPlugins:pluginType[] = [];
        let len = plugins.length;
        for (let i = 0; i < len; i++) {
            let plugin = await this.getPlugin(plugins[0])
            newPlugins.push(plugin);
        }
        return newPlugins;
    }

    /**
     * 加载插件
     */
    loadPlugin=(pluginNames: string|string[])=>{
        return new Promise(async (resolve)=>{
            let plugins: any[] = [];
            if(typeof pluginNames==='string'){
                pluginNames = [pluginNames];
            }
            if(!pluginNames || pluginNames.length<1){
                resolve(plugins);
                return;
            }

            let len = pluginNames.length;
            let unLoadPlugin = [];
            let unRegisterPlugin = [];
            for (let i = 0; i < len; i++) {
                const pluginName = pluginNames[i];
                if(!(pluginName && typeof pluginName==='string')) break;
                let cachePlugin = this._pluginCache[pluginName];
                if(cachePlugin){
                    const {loadStatus} = cachePlugin;
                    if(loadStatus==='loaded'){
                        plugins.push(cachePlugin)
                    }else {
                        unLoadPlugin.push(cachePlugin);
                    }
                }else {
                    unRegisterPlugin.push(pluginName)
                }
            }
            let syncPlugin = unLoadPlugin.filter(item=>item.sync).sort((a,b)=>(a.order||0)-(b.order||0));
            let unSyncPlugin = unLoadPlugin.filter(item=>!item.sync).sort((a,b)=>(a.order||0)-(b.order||0));
            if(syncPlugin.length){
                let syncPlugins = await this.loadSyncPlugin(syncPlugin);
                plugins = plugins.concat(syncPlugins)
            }
            let result = await Promise.allSettled([
                ...unRegisterPlugin.map(name=>this.getUnRegisterPlugin(name)),
                ...unSyncPlugin.map(plugin=>this.getPlugin(plugin))
            ]);
            plugins = plugins.concat(result.map((item: any)=>item.value))
            resolve(plugins);
        })
    }
}

const pluginManager = new PluginManager();
export default pluginManager;